import React, { useState } from "react";
import { optionsSearch } from "../../utils/search";
import { useTranslation } from "react-i18next";

import { Button, Divider, Select, Space, Tooltip, Typography } from "antd";

import { GRAY5, GRAY_PLACEHOLDER, WHITE } from "../../constant/colors";
import FloatLabel from "./FloatLabel";
const communStyle = (changed) => ({
  width: "100%",
  border: "0px",
  backgroundColor: "transparent",
  borderBottom: "1px solid #9B9B9B",
  color: changed ? "#2854A1" : "#9B9B9B"
});

const { Text } = Typography;
const formatTooltipContent = (id, tooltipContent) => {
  if (!tooltipContent || tooltipContent.length === 0) {
    return "";
  }

  return tooltipContent
    ?.map((item) => {
      return item.text || item.children;
    })
    .filter(Boolean)
    .join(" | ");
};

const MultiSelect = ({
  children,
  changed,
  disabled = false,
  maxTagCount = 1,
  dataTMId,
  filterOption = optionsSearch,
  style,
  tooltipContent = [],
  selectAll = false,
  clearAll = false,
  onClearAll,
  onSelectAll,
  onDropdownVisibleChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [isHovering, setHovering] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const formattedTooltipContent = formatTooltipContent(dataTMId, tooltipContent);

  const handleDropdownVisibleChange = (open) => {
    setDropdownOpen(open);
    if (onDropdownVisibleChange) {
      onDropdownVisibleChange(open);
    }
  };

  return (
    <div id={dataTMId}>
      <Tooltip
        open={formattedTooltipContent && !isDropdownOpen && isHovering}
        title={<Text>{formattedTooltipContent}</Text>}
        color={WHITE}
        placement="right"
        overlayStyle={{
          maxWidth: "calc(100% - 600px)",
          whiteSpace: "normal",
          wordWrap: "break-word"
        }}
      >
        <Select
          id={dataTMId}
          data-tm-id={dataTMId}
          mode="multiple"
          bordered={false}
          showArrow={true}
          allowClear={true}
          disabled={disabled}
          getPopupContainer={(trigger) => trigger.parentNode}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          filterOption={filterOption}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          style={{
            ...communStyle(changed),
            ...style
          }}
          maxTagCount={maxTagCount}
          dropdownRender={(menu) => (
            <div>
              {selectAll && clearAll && (
                <>
                  <div style={{ display: "flex", flexWrap: "nowrap", padding: 8, gap: 6 }}>
                    <Button block onClick={onSelectAll}>
                      {t("select_all")}
                    </Button>
                    <Button onClick={onClearAll}>{t("clear")}</Button>
                  </div>

                  <Divider style={{ margin: "4px 0" }} />
                </>
              )}
              {menu}
            </div>
          )}
          {...props}
        >
          {children}
        </Select>
      </Tooltip>
    </div>
  );
};

const SingleSelect = ({
  children,
  changed,
  disabled = false,
  style = {},
  dataTMId,
  filterOption = optionsSearch,
  showSearch = true,
  onClear,
  ...props
}) => {
  return (
    <div id={dataTMId}>
      <Select
        data-tm-id={dataTMId}
        id={dataTMId}
        bordered={false}
        disabled={disabled}
        filterOption={filterOption}
        showSearch={showSearch}
        allowClear={true}
        onClear={onClear}
        getPopupContainer={(trigger) => trigger.parentNode}
        style={{
          ...communStyle(changed),
          ...style
        }}
        {...props}
      >
        {children}
      </Select>
    </div>
  );
};

const SingleSelectWithFloatLabel = ({
  children,
  filterOption = optionsSearch,
  required = false,
  showSearch = true,
  bordered = false,
  useCommonStyle = true,
  style,
  ...props
}) => {
  const commonStyle = {
    width: "100%",
    border: `0px`,
    borderBottom: `1px solid ${GRAY5}`,
    padding: 0
  };
  return (
    <FloatLabel
      label={
        <Space size={4}>
          {required && <Text style={{ color: GRAY_PLACEHOLDER }}>*</Text>}
          <Text style={{ color: GRAY_PLACEHOLDER }}>{props.label}</Text>
        </Space>
      }
      name={props.label}
      inputValue={props.value}
    >
      <Select
        bordered={bordered}
        filterOption={filterOption}
        showSearch={showSearch}
        style={{
          ...(useCommonStyle && commonStyle),
          padding: props.label ? "6px 0 0 0" : "",
          ...style
        }}
        placeholder={props.placeholder && (required ? `* ${props.placeholder}` : props.placeholder)}
        {...props}
      >
        {children}
      </Select>
    </FloatLabel>
  );
};

export { SingleSelectWithFloatLabel, SingleSelect, MultiSelect };
